import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { ScrollTableOnScreenToTop } from '@lmt/unicorn-shared/app/helpers/devextreme-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useEffect, useCallback, useRef } from 'react';
import {
  HL7MessageDto,
  HL7MessageDirection,
  FileInfoDto,
} from '@lmt/shared-types';
import styles from '@lmt/unicorn-shared/app/components/patients/list/components/PatientsTable.module.scss';
import { TableDotMenu } from '../../../common/table/TableDotMenu';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { HL7DocumentItem } from './DocumentItem';
import { getFullName } from '../../../patients/patient-form/common/common';
import { useTranslation } from 'react-i18next';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { UniLink } from '../../../common/ui-kit/buttons/UniLink';
import { ButtonColor } from '../../../common/ui-kit/buttons/Button';
import Highlighter from 'react-highlight-words';
import localStyles from '../HL7MessagesPage.module.scss';
import { HL7MessageTypeNamesDict } from '@lmt/unicorn-shared/data/messageTypeNamesDict';
import {
  DXG,
  useDataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';

function dateToFormat(date: Date): string {
  return localFormat(date, 'Ppp');
}

function getFormattedDateTime(date?: Date | null): string {
  return date ? dateToFormat(date) : '';
}

type Props = {
  noResults: boolean;
  data: HL7MessageDto[];
  documents: FileInfoDto[];
  reparseMessage: (messageId: number) => void;
  reparseMessageWithFilter: (messageId: number) => void;
  onDelete: (messageId: number) => void;
  onDownloadMessage: (messageId: number) => void;
  onShowFullMessage: (messageId: number) => void;
  searchText?: string | null;
};

type RowData = {
  data: HL7MessageDto;
  value: any;
};

const nameof = nameofFactory<HL7MessageDto>();

export const MessagesTable: FC<Props> = React.memo(
  function MessagesTable(props) {
    useDataGrid();
    const { searchText, onShowFullMessage } = props;
    const i18n = useScopedTranslation('hl7.messages');
    const i18nGlobal = useTranslation();
    const tabs = useTabs();

    const openPatientProfile = useCallback((patientId: number) => {
      tabs.openTab(pages.patientProfile.link({ patientId }));
    }, []);

    useEffect(() => {
      ScrollTableOnScreenToTop();
    }, [props.data]);

    const RowDetail = useCallback(
      (props: any) => {
        const data = props.data.data;
        const isMessageLimited = data.content.length === 2400; // message content is cut by backend
        const content: string = data.content.replace(/(\r\n|\r|\n)/g, '\r\n');

        return (
          <>
            <div className={styles.content}>
              <Highlighter
                searchWords={searchText ? [searchText] : []}
                textToHighlight={
                  isMessageLimited ? content.concat('...') : content
                }
                autoEscape={true}
              />
            </div>
            {isMessageLimited && (
              <div className={styles.bottomLinkContainer}>
                <UniLink
                  title={i18n.t('options.show_full_message')}
                  color={ButtonColor.Primary}
                  onClick={() => onShowFullMessage(data.id)}
                />
              </div>
            )}
          </>
        );
      },
      [searchText],
    );

    if (props.noResults) {
      return (
        <div className={styles.emptyList}>
          <div className={styles.emptyListText}>
            {i18n.t('empty_list_text')}
          </div>
        </div>
      );
    }

    return (
      <DXG.DataGrid
        keyExpr="id"
        dataSource={props.data ?? []}
        width={'100%'}
        height={'100%'}
      >
        <DXG.MasterDetail enabled={true} component={RowDetail} />
        <DXG.Paging enabled={false} />
        <DXG.Column
          width="60px"
          alignment={'left'}
          dataField={nameof('id')}
          caption={i18n.t('columns.id')}
          allowSorting={false}
        />
        <DXG.Column
          width="10%"
          alignment={'left'}
          dataField={nameof('dateTime')}
          caption={i18n.t('columns.date_time')}
          cellRender={(celldata: RowData) => {
            return (
              <div className={localStyles.error}>
                {getFormattedDateTime(celldata.value)}
              </div>
            );
          }}
          allowSorting={false}
        />
        <DXG.Column
          width="15%"
          alignment={'left'}
          dataField={nameof('type')}
          caption={i18n.t('columns.type')}
          cellRender={(celldata: RowData) => {
            return (
              <div className={localStyles.error}>
                {HL7MessageTypeNamesDict[celldata.data.type]}
              </div>
            );
          }}
          allowSorting={false}
        />
        <DXG.Column
          width="8%"
          alignment={'left'}
          dataField={nameof('messageDirection')}
          caption={i18n.t('columns.message_direction')}
          calculateDisplayValue={(message: HL7MessageDto) => {
            return message.messageDirection === HL7MessageDirection.Incoming
              ? i18n.t('incoming_message_status')
              : i18n.t('outgoing_message_status');
          }}
          allowSorting={false}
        />
        <DXG.Column
          width="8%"
          alignment={'left'}
          dataField={nameof('status')}
          caption={i18n.t('columns.status')}
          calculateDisplayValue={(message: HL7MessageDto) =>
            i18n.t(`status.${message.status}` as any)
          }
          allowSorting={false}
        />
        <DXG.Column
          width="15%"
          alignment={'left'}
          dataField={nameof('patient')}
          caption={i18n.t('columns.patient')}
          cellRender={(celldata: RowData) => {
            return (
              <div
                onClick={(event: any) => {
                  event.stopPropagation();
                  celldata.data.patient &&
                    openPatientProfile(celldata.data.patient.id);
                }}
                className={styles.patientNameInfo}
              >
                {getFullName(celldata.value)}
              </div>
            );
          }}
          allowSorting={false}
        />
        <DXG.Column
          width="15%"
          dataField={nameof('outgoingFileId')}
          caption={i18n.t('columns.document')}
          cellRender={(celldata: RowData) => {
            return (
              <div onClick={(event: any) => event.stopPropagation()}>
                <HL7DocumentItem
                  document={props.documents.find(
                    (d) => d.id === celldata.data.outgoingFileId,
                  )}
                />
              </div>
            );
          }}
          allowSorting={false}
        />
        <DXG.Column
          alignment={'left'}
          dataField={nameof('error')}
          caption={i18n.t('columns.error')}
          cellRender={(celldata: RowData) => {
            return (
              <div className={localStyles.error}>
                {celldata.data.error &&
                  i18nGlobal.t(celldata.data.error as any)}
              </div>
            );
          }}
          allowSorting={false}
        />
        <DXG.Column
          width={50}
          caption={i18n.t('columns.actions')}
          cellRender={(celldata: RowData) => {
            return (
              <div onClick={(event: any) => event.stopPropagation()}>
                <TableDotMenu
                  iconClassName={localStyles['option-button']}
                  options={[
                    {
                      title: i18n.t('options.reparse_message'),
                      onClick: () => props.reparseMessage(celldata.data.id),
                      isHidden:
                        celldata.data.messageDirection !==
                        HL7MessageDirection.Incoming,
                    },
                    {
                      title: i18n.t('options.reparse_message_with_filter'),
                      onClick: () =>
                        props.reparseMessageWithFilter(celldata.data.id),
                      isHidden:
                        celldata.data.messageDirection !==
                        HL7MessageDirection.Incoming,
                    },
                    {
                      title: i18n.t('options.download'),
                      onClick: () => props.onDownloadMessage(celldata.data.id),
                    },
                    {
                      title: i18n.t('options.delete'),
                      onClick: () => props.onDelete(celldata.data.id),
                      isDeleteItem: true,
                      isUnderLine:
                        celldata.data.messageDirection ==
                        HL7MessageDirection.Incoming,
                    },
                  ]}
                />
              </div>
            );
          }}
        />
      </DXG.DataGrid>
    );
  },
);
